import React, { useMemo, useState } from 'react';
import { StoreIcon, LayoutGridIcon, ListIcon } from 'lucide-react';
import ContentPageHeader from '../components/Content/ContentPageHeader';
import { FilterSidebarWrapper, FilterSidebar, FilterSection } from '../components/UI/FilterSidebar';
import { SearchWithAutocomplete } from '../components/UI/SearchWithAutocomplete';
import ViewModeToggle from '../components/UI/ViewModeToggle';
import { BookstoreCard } from '../components/Bookstores/BookstoreCard';
import { bookstores } from '../data/bookstoresData';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { usePlatform } from '../context/PlatformContext';
const Bookstores = () => {
  const dk = useDarkClasses();
  const {
    isAfterTide
  } = usePlatform();
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  // Filter states
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  // Extract unique filter options from data
  const allCountries = Array.from(new Set(bookstores.map(s => s.country))).sort();
  const allGenres = Array.from(new Set(bookstores.flatMap(s => s.specializations))).sort();
  const filterSections: FilterSection[] = [{
    id: 'type',
    title: 'Store Type',
    type: 'checkbox',
    value: selectedTypes,
    onChange: val => {
      setSelectedTypes(prev => prev.includes(val) ? prev.filter(t => t !== val) : [...prev, val]);
    },
    options: [{
      value: 'brick-and-mortar',
      label: 'Brick & Mortar'
    }, {
      value: 'online-only',
      label: 'Online Only'
    }, {
      value: 'hybrid',
      label: 'Hybrid (Physical + Online)'
    }, {
      value: 'pop-up',
      label: 'Pop-up / Event'
    }, {
      value: 'specialty',
      label: 'Specialty Store'
    }]
  }, {
    id: 'country',
    title: 'Country',
    type: 'checkbox',
    value: selectedCountries,
    onChange: val => {
      setSelectedCountries(prev => prev.includes(val) ? prev.filter(c => c !== val) : [...prev, val]);
    },
    options: allCountries.map(c => ({
      value: c,
      label: c
    }))
  }, {
    id: 'genres',
    title: 'Specializations',
    type: 'checkbox',
    value: selectedGenres,
    onChange: val => {
      setSelectedGenres(prev => prev.includes(val) ? prev.filter(g => g !== val) : [...prev, val]);
    },
    options: allGenres.map(g => ({
      value: g,
      label: g
    }))
  }, {
    id: 'features',
    title: 'Features',
    type: 'checkbox',
    value: selectedFeatures,
    onChange: val => {
      setSelectedFeatures(prev => prev.includes(val) ? prev.filter(f => f !== val) : [...prev, val]);
    },
    options: [{
      value: 'signedCopies',
      label: 'Signed Copies'
    }, {
      value: 'hostsEvents',
      label: 'Hosts Events'
    }, {
      value: 'indieFocused',
      label: 'Indie-Focused'
    }, {
      value: 'internationalShipping',
      label: 'International Shipping'
    }]
  }];
  const handleResetFilters = () => {
    setSelectedTypes([]);
    setSelectedCountries([]);
    setSelectedGenres([]);
    setSelectedFeatures([]);
    setSearchQuery('');
  };
  // Apply filters and search
  const filteredBookstores = useMemo(() => {
    return bookstores.filter(store => {
      // Search
      if (searchQuery && !store.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Type
      if (selectedTypes.length > 0 && !selectedTypes.includes(store.type)) {
        return false;
      }
      // Country
      if (selectedCountries.length > 0 && !selectedCountries.includes(store.country)) {
        return false;
      }
      // Genres
      if (selectedGenres.length > 0 && !selectedGenres.some(g => store.specializations.includes(g))) {
        return false;
      }
      // Features
      if (selectedFeatures.length > 0) {
        const hasAllFeatures = selectedFeatures.every(f => store.features[f as keyof typeof store.features]);
        if (!hasAllFeatures) return false;
      }
      return true;
    }).sort((a, b) => {
      if (sortBy === 'popular') return b.reviewCount - a.reviewCount;
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'books') return b.bookCount - a.bookCount;
      return 0;
    });
  }, [searchQuery, selectedTypes, selectedCountries, selectedGenres, selectedFeatures, sortBy]);
  return <div className={`min-h-screen ${dk.page}`} data-id="element-8667" data-mp="3b2yvjinw1yhe">
      <ContentPageHeader title="Bookstore Directory" description="Discover independent bookstores, online retailers, and specialty shops that carry your favorite books." icon={<StoreIcon className={`h-8 w-8 ${isAfterTide ? 'text-[#e2b04a]' : 'text-indigo-500'}`} data-id="element-8669" data-mp="2wf40vwqblk1o" />} data-id="element-8668" data-mp="3b5urj1g6ed37" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-8670" data-mp="3turqvjqfjvsa">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-8671" data-mp="3p3rohyyjklie">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={isMobileFiltersOpen} onClose={() => setIsMobileFiltersOpen(false)} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} data-id="element-8672" data-mp="3p1yhgik0nz9v">
            <FilterSidebar sections={filterSections} onReset={handleResetFilters} data-id="element-8673" data-mp="1dfwn4cxkznhq" />
          </FilterSidebarWrapper>

          {/* Main Content */}
          <div className="flex-1 min-w-0" data-id="element-8674" data-mp="14eyiptj8ddym">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-8675" data-mp="18huw95twyjvp">
              <div className="w-full sm:w-96" data-id="element-8676" data-mp="uid90uw9neog">
                <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} suggestions={[]} placeholder="Search bookstores by name..." showIcon={true} data-id="element-8677" data-mp="ujocsuppomfk" />
              </div>

              <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end" data-id="element-8678" data-mp="2ttwj5ctb2szp">
                <button onClick={() => setIsMobileFiltersOpen(true)} className={`lg:hidden px-4 py-2 rounded-lg text-sm font-medium border ${dk.isDark ? 'border-gray-700 bg-gray-800 text-gray-200' : 'border-gray-300 bg-white text-gray-700'}`} data-id="element-8679" data-mp="i589gldqmxes">
                  Filters
                </button>

                <div className="flex items-center gap-3" data-id="element-8680" data-mp="10x10ia98y8jv">
                  <select value={sortBy} onChange={e => setSortBy(e.target.value)} className={`text-sm rounded-lg border-transparent focus:ring-0 cursor-pointer ${dk.isDark ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-700'}`} data-id="element-8681" data-mp="10znaegjm1059">
                    <option value="popular" data-id="element-8682" data-mp="3cn3blutr0cgc">Most Popular</option>
                    <option value="rating" data-id="element-8683" data-mp="3cnmsd87nwuss">Highest Rated</option>
                    <option value="books" data-id="element-8684" data-mp="37pbk31x0msco">Most Books</option>
                  </select>

                  <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} data-id="element-8685" data-mp="2lvw6yw21ef3u" />
                </div>
              </div>
            </div>

            {/* Results */}
            {filteredBookstores.length > 0 ? <div className={viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6' : 'flex flex-col gap-4'} data-id="element-8686" data-mp="30k0o3vlfyiki">
                {filteredBookstores.map(store => <BookstoreCard key={store.id} bookstore={store} viewMode={viewMode} data-id="element-8687" data-mp="2cuo6q8fyn474" />)}
              </div> : <div className={`text-center py-16 rounded-xl border ${dk.card}`} data-id="element-8688" data-mp="3oacuv34ya99i">
                <StoreIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-8689" data-mp="3sandbf9xis59" />
                <h3 className={`text-lg font-medium mb-2 ${dk.textPrimary}`} data-id="element-8690" data-mp="1go32grdel5dr">
                  No bookstores found
                </h3>
                <p className={dk.textSecondary} data-id="element-8691" data-mp="1cn1a8uhwltla">
                  Try adjusting your filters or search query.
                </p>
                <button onClick={handleResetFilters} className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium ${isAfterTide ? 'bg-[#e2b04a] text-[#1a1a2e]' : 'bg-indigo-100 text-indigo-700'}`} data-id="element-8692" data-mp="12wg3ukvznkrt">
                  Clear all filters
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default Bookstores;